import { rem } from 'polished'
import { useCallback, useEffect, useRef } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Clickable from 'components/Common/Clickable'
import IconButton from 'LuxKit/Button/IconButton'
import BaseImageCarousel from 'LuxKit/Carousel/BaseImageCarousel'
import type { ImageCarouselRefInterface } from 'LuxKit/Carousel/BaseImageCarousel'
import useCarouselArrowKind from 'LuxKit/Carousel/useCarouselArrowKind'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import type { ImageParams } from 'components/Common/ResponsiveImage'

const viewerImageProps: ImageParams = { mobileAspectRatio: '16:9', quality: 'good', fit: 'center' }

const ViewerLayout = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${rem(16)};
  padding: ${rem(22)} 0;
  background: ${props => props.theme.palette.neutral.surface.darkBackground};
  position: relative;
`

const ImageArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  flex: 1;
`

const ImageFrame = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: ${rem(1280)};
  max-height: 100%;
  aspect-ratio: 16 / 9;
  margin: 0 auto;
`

const CloseButton = styled(Clickable)`
  position: absolute;
  top: ${rem(20)};
  inset-inline-end: ${rem(20)};
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${rem(8)};
  width: ${rem(40)};
  height: ${rem(40)};
  padding-block: ${rem(12)};
  padding-inline: ${rem(8)};
  border-radius: ${props => props.theme.borderRadius['2XL']};
  background: ${props => props.theme.palette.neutral.default.eight};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  color: ${props => props.theme.palette.neutral.default.one};
  cursor: pointer;
`

const NavigationArrow = styled(IconButton)`
  display: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;

  ${mediaQueryUp.tablet} {
    display: inline-flex;
  }

  &[hidden] {
    opacity: 0;
    pointer-events: none;
  }
`

const PreviousArrow = styled(NavigationArrow)`
  inset-inline-start: ${rem(20)};
`

const NextArrow = styled(NavigationArrow)`
  inset-inline-end: ${rem(20)};
`

interface Props {
  images: Array<App.Image>
  defaultIndex?: number
  onClose: () => void
  onImageChange?: (index: number) => void
}

function GalleryV2ImageViewer(props: Props) {
  const { images, defaultIndex = 0, onClose, onImageChange } = props
  const carouselRef = useRef<ImageCarouselRefInterface>(null)
  const intl = useIntl()
  const arrowKind = useCarouselArrowKind()
  const hasMultipleImages = images.length > 1

  const handleBackgroundMouseDown = useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (event.target === event.currentTarget) {
      event.stopPropagation()
      onClose()
    }
  }, [onClose])

  const handleCarouselImageChange = useCallback((index: number) => {
    onImageChange?.(index)
  }, [onImageChange])

  const handlePreviousClick = useCallback(() => {
    carouselRef.current?.previousImage()
  }, [])

  const handleNextClick = useCallback(() => {
    carouselRef.current?.nextImage()
  }, [])

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'ArrowLeft') {
        carouselRef.current?.previousImage()
      } else if (event.key === 'ArrowRight') {
        carouselRef.current?.nextImage()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  return <ViewerLayout onMouseDown={handleBackgroundMouseDown}>
    <CloseButton
      onClick={onClose}
      aria-label={intl.formatMessage({
        id: 'qHTZ-CTkPwcMmNyK-PnE',
        defaultMessage: 'Close photo',
      })}
    >
      <LineTimesIcon />
    </CloseButton>
    <PreviousArrow
      kind={arrowKind}
      variant="dark"
      shape="circle"
      size="medium"
      onClick={handlePreviousClick}
      hidden={!hasMultipleImages}
      aria-label={intl.formatMessage({
        id: '9L21gOAEY65NufyptS4E',
        defaultMessage: 'Previous image',
      })}
    >
      <LineAngleLeftIcon />
    </PreviousArrow>
    <ImageArea onMouseDown={handleBackgroundMouseDown}>
      <ImageFrame>
        <BaseImageCarousel
          images={images}
          defaultIndex={defaultIndex}
          carouselRef={carouselRef}
          imageParams={viewerImageProps}
          onImageChange={handleCarouselImageChange}
        />
      </ImageFrame>
    </ImageArea>
    <NextArrow
      kind={arrowKind}
      variant="dark"
      shape="circle"
      size="medium"
      onClick={handleNextClick}
      hidden={!hasMultipleImages}
      aria-label={intl.formatMessage({
        id: 'oA2DuwlPljrs5dA__1Ke',
        defaultMessage: 'Next image',
      })}
    >
      <LineAngleRightIcon />
    </NextArrow>
  </ViewerLayout>
}

export default GalleryV2ImageViewer
